import { useState, useContext, useCallback } from 'react';
import { AppContext } from 'src/AppContext';

import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useBoolean } from 'src/hooks/use-boolean';
import { useResponsive } from 'src/hooks/use-responsive';

import Iconify from 'src/components/iconify';

const defaultValues = {
  filterExpertise: [],
  filterPrice: [],
  filterTime: [],
  filterRegion: [],
  filterLanguage: [],
}

const EXPERTISE_OPTIONS = [
  "Bulk up",
  "Fat Loss",
  "Body Toning",
  "Cardiovascular Fitness",
  "Rehab Training",
  "General Fitness",
  "Pre/post Natal Training",
];
const PRICING_OPTIONS = ["80", "100", "120"];
const TIME_OPTIONS = ["morning", "afternoon", "evening"];
const REGION_OPTIONS = ["north", "south", "east", "west", "central"];
const LANGUAGE_OPTIONS = ["chinese", "english", "malay", "tamil"];

export default function ProfileFilter({ open, onClose, onChangeFilter, openSnackBar }) {
  const mdUp = useResponsive('up', 'md');
  const { user } = useContext(AppContext);
  const [filters, setFilters] = useState(defaultValues);

  const getSelected = (selectedItems, item, max_selection = -1) => {
    if (!user) {
      openSnackBar("Please sign in to unlock filter");
      return selectedItems;
    }

    if (selectedItems.includes(item)) {
      // remove item.
      return selectedItems.filter((value) => value !== item);
    }
    if (max_selection == -1) {
      // no limitation on selection.
      return [...selectedItems, item];
    }
    return [...(selectedItems.slice(0, max_selection - 1)), item];
  }

  const handleChangeExpertise = useCallback(
    (name) => {
      const result = {
        ...filters,
        filterExpertise: getSelected(filters.filterExpertise, name, 1),
      };
      setFilters(result);
      onChangeFilter(result);
    },
    [filters, onChangeFilter]
  );

  const handleChangePricing = useCallback(
    (name) => {
      const result = {
        ...filters,
        filterPrice: getSelected(filters.filterPrice, name),
      };
      setFilters(result);
      onChangeFilter(result);
    },
    [filters, onChangeFilter]
  );

  const handleChangeTime = useCallback(
    (name) => {
      const result = {
        ...filters,
        filterTime: getSelected(filters.filterTime, name, 1),
      };
      setFilters(result);
      onChangeFilter(result);
    },
    [filters, onChangeFilter]
  );

  const handleChangeRegion = useCallback(
    (name) => {
      const result = {
        ...filters,
        filterRegion: getSelected(filters.filterRegion, name, 1),
      };
      setFilters(result);
      onChangeFilter(result);
    },
    [filters, onChangeFilter]
  );

  const handleChangeLanguage = useCallback(
    (name) => {
      const result = {
        ...filters,
        filterLanguage: getSelected(filters.filterLanguage, name),
      };
      setFilters(result);
      onChangeFilter(result);
    },
    [filters, onChangeFilter]
  );

  const handleClearAll = useCallback(() => {
    setFilters(defaultValues);
    onChangeFilter(defaultValues);
  }, [onChangeFilter]);

  const renderContent = (
    <Stack
      spacing={{ xs: 2, md: 3 }}
      alignItems="flex-start"
      sx={{
        flexShrink: 0,
        width: { xs: 1, md: 280 },
        mb: 2,
      }}
    >
      <Block title="Expertise">
        <FilterMulCheckBoxes
          checked={filters.filterExpertise}
          onChangeFilter={handleChangeExpertise}
          options={EXPERTISE_OPTIONS}
          sx={{ mt: 1 }}
        />
      </Block>

      <Block title="Pricing">
        <FilterMulChips
          checked={filters.filterPrice}
          onChangeFilter={handleChangePricing}
          options={PRICING_OPTIONS}
          mapOptionLabel={(option) => "S$" + option}
          sx={{ mt: 1 }}
        />
      </Block>

      <Block title="Time">
        <FilterMulCheckBoxes
          checked={filters.filterTime}
          onChangeFilter={handleChangeTime}
          options={TIME_OPTIONS}
          mapOptionLabel={(option) => titleCase(option)}
          sx={{ mt: 1 }}
        />
      </Block>

      <Block title="Region">
        <FilterMulChips
          checked={filters.filterRegion}
          onChangeFilter={handleChangeRegion}
          options={REGION_OPTIONS}
          mapOptionLabel={(option) => titleCase(option)}
          sx={{ mt: 1 }}
        />
      </Block>

      <Block title="Language">
        <FilterMulChips
          checked={filters.filterLanguage}
          onChangeFilter={handleChangeLanguage}
          options={LANGUAGE_OPTIONS}
          mapOptionLabel={(option) => titleCase(option)}
          sx={{ mt: 1 }}
        />
      </Block>

      <Button
        fullWidth
        color="inherit"
        size="large"
        variant="contained"
        startIcon={<Iconify icon="carbon:trash-can" />}
        onClick={handleClearAll}
      >
        Clear All
      </Button>
    </Stack>
  );

  return (
    <>
      {mdUp ? (
        renderContent
      ) : (
        <Drawer
          anchor="right"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              pt: 3,
              px: 3,
              width: 280,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </>
  );
}

function FilterMulCheckBoxes({ options, checked, onChangeFilter, mapOptionLabel, ...other }) {
  return (
    <Stack {...other}>
      {options.map((option) => (
        <FormControlLabel
          key={option}
          control={
            <Checkbox
              size="small"
              value={option}
              checked={checked.includes(option)}
              onChange={() => onChangeFilter(option)}
            />
          }
          label={mapOptionLabel ? mapOptionLabel(option) : option}
        />
      ))}
    </Stack>
  );
}

function FilterMulChips({ options, checked, onChangeFilter, mapOptionLabel, ...other }) {
  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap {...other}>
      {options.map((option) => {
        const selected = checked.includes(option);

        return (
          <Chip
            key={option}
            label={mapOptionLabel ? mapOptionLabel(option) : option}
            variant="outlined"
            onClick={() => onChangeFilter(option)}
            sx={{
              ...(selected && {
                bgcolor: 'action.selected',
                fontWeight: 'fontWeightBold',
              }),
              margin: 0.5,
            }}
          />
        );
      })}
    </Stack>
  );
}

function Block({ title, children, ...other }) {
  const contentOpen = useBoolean(true);

  return (
    <Stack alignItems="flex-start" sx={{ width: 1 }} {...other}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        onClick={contentOpen.onToggle}
        sx={{ width: 1, cursor: 'pointer' }}
      >
        <Typography variant="h6">{title}</Typography>

        <Iconify
          icon={contentOpen.value ? 'carbon:subtract' : 'carbon:add'}
          sx={{ color: 'text.secondary' }}
        />
      </Stack>

      <Collapse unmountOnExit in={contentOpen.value} sx={{ px: 0.5 }}>
        {children}
      </Collapse>
    </Stack>
  );
}

function titleCase(str) {
  var splitStr = str.toLowerCase().split(' ');
  for (var i = 0; i < splitStr.length; i++) {
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  return splitStr.join(' ');
}
